"use client";

import { AtSign, Users, Megaphone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { getInitials } from "./helpers";
import type { ChannelMember } from "./TeamChatService";

/** Represents an agency group that can be @mentioned in announcement channels */
export interface AgencyMentionOption {
  id: string;
  name: string;
  type: "agency";
}

export interface MentionAutocompleteProps {
  query: string;
  members: ChannelMember[];
  onSelect: (member: ChannelMember) => void;
  selectedIndex: number;
  /** Only owners/admins can use @everyone */
  isOwnerOrAdmin?: boolean;
  /** When in an announcement channel, show agency groups as mentionable targets */
  isAnnouncementChannel?: boolean;
  /** List of agencies in the channel (for agency-level @mentions) */
  agencyList?: Array<{ id: string; name: string }>;
  /** Called when an agency group mention is selected */
  onSelectAgency?: (agency: AgencyMentionOption) => void;
}

export function MentionAutocomplete({
  query,
  members,
  onSelect,
  selectedIndex,
  isOwnerOrAdmin,
  isAnnouncementChannel,
  agencyList,
  onSelectAgency,
}: MentionAutocompleteProps) {
  const q = query.toLowerCase();

  // Special mentions (@everyone) — only for owners/admins
  const specialMentions = isOwnerOrAdmin ? [
    { id: "everyone", name: "everyone", label: "Notify all channel members", icon: "📢" },
  ].filter(s => !q || s.name.includes(q)) : [];

  const filtered = members.filter(m =>
    (m.user.name || m.user.email || "").toLowerCase().includes(q)
  ).slice(0, 8);

  // Build agency group options for announcement channels
  const agencyOptions: AgencyMentionOption[] = [];
  if (isAnnouncementChannel && agencyList && agencyList.length > 0) {
    for (const agency of agencyList) {
      if (!q || agency.name.toLowerCase().includes(q)) {
        agencyOptions.push({ id: agency.id, name: agency.name, type: "agency" });
      }
    }
  }

  // Total items: special mentions first, then agency groups, then individual members
  const totalItems = specialMentions.length + agencyOptions.length + filtered.length;
  if (totalItems === 0) return null;

  return (
    <div role="listbox" aria-label="Mention suggestions" id="mention-autocomplete" className="absolute bottom-full left-0 right-0 mb-2 mx-3 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl overflow-hidden z-20">
      <div className="px-3 py-1.5 border-b border-zinc-100 dark:border-zinc-700">
        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-1">
          <AtSign className="w-3 h-3" /> Mention someone
        </span>
      </div>

      {/* Special mentions (@everyone) — always at the top */}
      {specialMentions.map((s, i) => (
        <button
          key={`special-${s.id}`}
          role="option"
          aria-selected={i === selectedIndex}
          onClick={() => {
            // Insert @everyone directly — treated as a keyword by the backend
            const fakeMember = { userId: s.id, user: { name: s.name, email: "", id: s.id } } as ChannelMember;
            onSelect(fakeMember);
          }}
          className={`w-full flex items-center gap-3 px-3 py-2 transition-colors text-left ${
            i === selectedIndex ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
          }`}
        >
          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800/40 flex items-center justify-center text-sm">
            {s.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">@{s.name}</p>
            <p className="text-[10px] text-zinc-400">{s.label}</p>
          </div>
        </button>
      ))}

      {/* Agency group mentions — shown for announcement channels */}
      {agencyOptions.length > 0 && (
        <>
          <div className="px-3 py-1 bg-amber-50/50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-800/30">
            <span className="text-[9px] font-semibold text-amber-500 uppercase tracking-wider flex items-center gap-1">
              <Megaphone className="w-2.5 h-2.5" /> Agency Groups
            </span>
          </div>
          {agencyOptions.map((agency, i) => {
            const globalIdx = specialMentions.length + i;
            return (
              <button
                key={`agency-${agency.id}`}
                role="option"
                aria-selected={globalIdx === selectedIndex}
                onClick={() => onSelectAgency?.(agency)}
                className={`w-full flex items-center gap-3 px-3 py-2 transition-colors text-left ${
                  globalIdx === selectedIndex ? "bg-amber-50 dark:bg-amber-900/20" : "hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">@{agency.name}</p>
                </div>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">AGENCY</span>
              </button>
            );
          })}
        </>
      )}

      {/* Individual member mentions */}
      {filtered.map((m, i) => {
        const globalIndex = specialMentions.length + agencyOptions.length + i;
        return (
          <button
            key={m.userId}
            id={`mention-option-${m.userId}`}
            role="option"
            aria-selected={globalIndex === selectedIndex}
            onClick={() => onSelect(m)}
            className={`w-full flex items-center gap-3 px-3 py-2 transition-colors text-left ${
              globalIndex === selectedIndex ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
            }`}
          >
            <Avatar className="w-6 h-6">
              <AvatarImage src={m.user.profileImageUrl} />
              <AvatarFallback className="text-[9px] bg-blue-600 text-white font-semibold">
                {getInitials(m.user.name || m.user.email)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">{m.user.name || m.user.email}</p>
            </div>
            {m.agencyRole === "owner" && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">OWNER</span>
            )}
            {m.agencyRole === "admin" && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">ADMIN</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
